<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { ArrowLeft, ArrowRight, CheckCircle2, LoaderCircle, Volume2 } from 'lucide-vue-next';
import { useRoute, useRouter } from 'vue-router';
import AudioRecorder from '@/components/AudioRecorder.vue';
import { cl_api, cl_errorMessage } from '@/api';
import { useLearnerStore } from '@/stores/learner';
import type { ClAssessment, ClLesson } from '@/types';

const cl_route = useRoute();
const cl_router = useRouter();
const cl_learner = useLearnerStore();
const cl_lesson = ref<ClLesson | null>(null);
const cl_index = ref(0);
const cl_blob = ref<Blob | null>(null);
const cl_result = ref<ClAssessment | null>(null);
const cl_sessionId = ref('');
const cl_status = ref<'idle' | 'uploading' | 'analyzing'>('idle');
const cl_error = ref('');
const cl_playing = ref(false);
let cl_audio: HTMLAudioElement | null = null;

const cl_current = computed(() => cl_lesson.value?.sentences[cl_index.value] ?? null);
const cl_maxSeconds = computed(() => Math.min(20, Math.max(6, (cl_current.value?.text.split(/\s+/).length ?? 3) * 2.2)));
const cl_progress = computed(() => cl_lesson.value?.sentences.length ? ((cl_index.value + 1) / cl_lesson.value.sentences.length) * 100 : 0);

onMounted(async () => {
  try {
    cl_lesson.value = (await cl_api.get(`/courses/lessons/${String(cl_route.params.cl_slug)}`)).data;
    cl_sessionId.value = (await cl_api.post('/learning/sessions', { cl_childId: cl_learner.cl_activeChildId, cl_lessonId: cl_lesson.value?.id, cl_type: 'READING' })).data.id;
  } catch (cl_cause) {
    cl_error.value = cl_errorMessage(cl_cause);
  }
});

onBeforeUnmount(() => {
  cl_audio?.pause();
  window.speechSynthesis?.cancel();
});

async function cl_playStandard() {
  if (!cl_current.value || cl_playing.value) return;
  cl_playing.value = true;
  cl_error.value = '';
  try {
    const cl_response = await cl_api.get('/pronunciation/tts', { params: { cl_childId: cl_learner.cl_activeChildId, cl_sentenceId: cl_current.value.id }, responseType: 'blob' });
    const cl_url = URL.createObjectURL(cl_response.data);
    cl_audio = new Audio(cl_url);
    cl_audio.onended = () => { cl_playing.value = false; URL.revokeObjectURL(cl_url); };
    await cl_audio.play();
  } catch {
    if (!('speechSynthesis' in window) || !('SpeechSynthesisUtterance' in window)) {
      cl_playing.value = false;
      cl_error.value = '当前设备无法播放标准发音。';
      return;
    }
    const cl_voice = new SpeechSynthesisUtterance(cl_current.value.text);
    cl_voice.lang = cl_learner.cl_activeChild?.profile.accent === 'EN_GB' ? 'en-GB' : 'en-US';
    cl_voice.rate = 0.78;
    cl_voice.onend = () => { cl_playing.value = false; };
    cl_voice.onerror = () => { cl_playing.value = false; };
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(cl_voice);
  }
}

function cl_recorded(cl_recording: Blob) {
  cl_blob.value = cl_recording;
  cl_result.value = null;
  cl_error.value = '';
}

async function cl_analyze() {
  if (!cl_blob.value || !cl_current.value) return;
  cl_status.value = 'uploading';
  cl_error.value = '';
  try {
    const cl_form = new FormData();
    cl_form.append('cl_audio', cl_blob.value, `sentence.${cl_blob.value.type.includes('mp4') ? 'm4a' : 'webm'}`);
    cl_form.append('cl_childId', cl_learner.cl_activeChildId);
    cl_form.append('cl_sentenceId', cl_current.value.id);
    cl_form.append('cl_sessionId', cl_sessionId.value);
    window.setTimeout(() => { if (cl_status.value === 'uploading') cl_status.value = 'analyzing'; }, 300);
    cl_result.value = (await cl_api.post('/pronunciation/assess', cl_form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 45_000 })).data;
  } catch (cl_cause) {
    cl_error.value = cl_errorMessage(cl_cause);
  } finally {
    cl_status.value = 'idle';
  }
}

function cl_next() {
  cl_index.value += 1;
  cl_blob.value = null;
  cl_result.value = null;
  cl_error.value = '';
}

async function cl_finish() {
  if (cl_sessionId.value) await cl_api.patch(`/learning/sessions/${cl_sessionId.value}/complete`, { cl_childId: cl_learner.cl_activeChildId });
  await cl_router.push('/');
}
</script>

<template>
  <section class="cl-practice-page">
    <header class="cl-practice-header"><RouterLink :to="`/lesson/${String(cl_route.params.cl_slug)}`" class="cl-icon-button"><ArrowLeft /></RouterLink><div><span>Sentence Reading</span><div class="cl-progress"><i :style="{ width: `${cl_progress}%` }" /></div></div><strong>{{ cl_index + 1 }}/{{ cl_lesson?.sentences.length ?? 1 }}</strong></header>
    <p v-if="cl_error" class="cl-form-error cl-practice-error">{{ cl_error }}</p>
    <div v-if="cl_current" class="cl-sentence-stage">
      <p class="cl-eyebrow">Read the sentence</p><h1>{{ cl_current.text }}</h1>
      <button class="cl-listen-button" :disabled="cl_playing" @click="cl_playStandard"><LoaderCircle v-if="cl_playing" class="cl-spin" /><Volume2 v-else /> Listen</button>
      <AudioRecorder :key="`${cl_current.id}:${cl_result?.cl_attemptId ?? ''}`" :cl_max-seconds="cl_maxSeconds" @cl_ready="cl_recorded" @cl_error="cl_error = $event" />
      <button v-if="cl_blob && !cl_result" class="cl-primary-button" :disabled="cl_status !== 'idle'" @click="cl_analyze"><LoaderCircle v-if="cl_status !== 'idle'" class="cl-spin" />{{ cl_status === 'idle' ? '分析句子' : cl_status === 'uploading' ? 'Uploading...' : 'Analyzing...' }}</button>
      <div v-if="cl_result" class="cl-sentence-result">
        <div><span>Accuracy<strong>{{ Math.round(cl_result.cl_providerScores.cl_accuracyScore) }}</strong></span><span>Fluency<strong>{{ Math.round(cl_result.cl_providerScores.cl_fluencyScore) }}</strong></span><span>Completeness<strong>{{ Math.round(cl_result.cl_providerScores.cl_completenessScore) }}</strong></span></div>
        <p>{{ cl_result.cl_status === 'MASTERED' ? '读得非常清楚！' : cl_result.cl_status === 'GOOD' ? '很好，再保持自然节奏。' : '再听一次，放慢速度完整读出每个词。' }}</p>
        <button v-if="cl_index < (cl_lesson?.sentences.length ?? 0) - 1" class="cl-primary-button" @click="cl_next">下一句 <ArrowRight /></button><button v-else class="cl-primary-button" @click="cl_finish">完成 <CheckCircle2 /></button>
      </div>
    </div>
  </section>
</template>
